/* KundaliChart — renders SVG in North Indian (diamond) or South Indian (square) style.
   Planets are abbreviated (Su, Mo, Ma, Me, Ju, Ve, Sa, Ra, Ke, As). */

const PLANET_SHORT = {
    Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
    Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

const SIGN_SHORT = [
    'Ar', 'Ta', 'Ge', 'Cn', 'Le', 'Vi', 'Li', 'Sc', 'Sg', 'Cp', 'Aq', 'Pi',
];

const COLOR = {
    bg: '#0F0F13',
    line: '#B45309',
    planet: '#FDE68A',
    house: '#94A3B8',
    zodiac: '#F59E0B',
    asc: '#F59E0B',
};

function groupByHouse(chart) {
    const out = Array.from({ length: 12 }, () => []);
    for (const p of chart.planets) {
        const h = (p.house || 1) - 1;
        out[h].push(p);
    }
    return out;
}

export default function KundaliChart({ chart, style = 'north', size = 380 }) {
    if (!chart?.planets || !chart?.houses) {
        return <div className="text-slate-500 text-sm">Chart data unavailable</div>;
    }
    return style === 'south' ? (
        <SouthIndian chart={chart} size={size} />
    ) : (
        <NorthIndian chart={chart} size={size} />
    );
}

function NorthIndian({ chart, size }) {
    // North Indian: square with diamond inside; 12 triangular/diamond compartments.
    // Ascendant (1st house) always at top-center; subsequent houses go anticlockwise.
    // Coordinate system: viewBox 300x300
    const S = 300;
    const houses = groupByHouse(chart);
    const ascSignIdx = chart.ascendant?.sign_idx ?? 0;

    // North Indian house index -> polygon centroid
    // House 1 centre (top), then 2 (top-left corner), 3 (left-top), ... anticlockwise
    // We use approximate centroids for 12 compartments.
    const POS = [
        [150, 80],   // 1 top
        [75, 55],    // 2 top-left upper
        [55, 75],    // 3 left-top corner
        [80, 150],   // 4 left
        [55, 225],   // 5 left-bottom corner
        [75, 245],   // 6 bottom-left lower
        [150, 220],  // 7 bottom
        [225, 245],  // 8 bottom-right lower
        [245, 225],  // 9 right-bottom corner
        [220, 150],  // 10 right
        [245, 75],   // 11 right-top corner
        [225, 55],   // 12 top-right upper
    ];

    // Compute sign for each house: ascendant sign in house 1, then +1 each house
    const signForHouse = (h) => SIGN_SHORT[(ascSignIdx + (h - 1)) % 12];

    return (
        <svg viewBox={`0 0 ${S} ${S}`} width={size} height={size} data-testid="kundali-chart-north" className="mx-auto">
            <rect x="0" y="0" width={S} height={S} fill={COLOR.bg} />
            {/* Outer square */}
            <rect x="10" y="10" width={S - 20} height={S - 20} fill="none" stroke={COLOR.line} strokeWidth="1.5" />
            {/* Inner diamond — 4 lines: both diagonals + cross connecting midpoints */}
            <line x1="10" y1="10" x2={S - 10} y2={S - 10} stroke={COLOR.line} strokeWidth="1.2" />
            <line x1={S - 10} y1="10" x2="10" y2={S - 10} stroke={COLOR.line} strokeWidth="1.2" />
            <line x1={S / 2} y1="10" x2="10" y2={S / 2} stroke={COLOR.line} strokeWidth="1.2" />
            <line x1="10" y1={S / 2} x2={S / 2} y2={S - 10} stroke={COLOR.line} strokeWidth="1.2" />
            <line x1={S / 2} y1={S - 10} x2={S - 10} y2={S / 2} stroke={COLOR.line} strokeWidth="1.2" />
            <line x1={S - 10} y1={S / 2} x2={S / 2} y2="10" stroke={COLOR.line} strokeWidth="1.2" />

            {POS.map((pt, i) => {
                const h = i + 1;
                const planets = houses[i];
                const [cx, cy] = pt;
                return (
                    <g key={h}>
                        <text x={cx} y={cy - 18} fill={COLOR.zodiac} fontSize="10"
                            fontFamily="Cormorant Garamond" textAnchor="middle" opacity="0.85">
                            {signForHouse(h)}
                        </text>
                        <text x={cx} y={cy - 6} fill={COLOR.house} fontSize="8"
                            textAnchor="middle" opacity="0.6">
                            {h}
                        </text>
                        {planets.map((p, idx) => (
                            <text
                                key={p.name}
                                x={cx}
                                y={cy + 8 + idx * 11}
                                fill={p.retrograde ? '#EF4444' : COLOR.planet}
                                fontSize="11"
                                fontFamily="Cormorant Garamond"
                                textAnchor="middle"
                                fontWeight="500"
                            >
                                {PLANET_SHORT[p.name] || p.name.slice(0, 2)}
                                {p.retrograde ? '℞' : ''}
                            </text>
                        ))}
                        {h === 1 && (
                            <text x={cx} y={cy - 32} fill={COLOR.asc} fontSize="9"
                                textAnchor="middle" opacity="0.95" fontFamily="Cormorant Garamond">
                                Asc
                            </text>
                        )}
                    </g>
                );
            })}
        </svg>
    );
}

function SouthIndian({ chart, size }) {
    // South Indian: signs have fixed positions; houses rotate based on ascendant.
    // Fixed 4x4 grid with center empty. Sign order (clockwise from top-left): Pi, Ar, Ta, Ge, ..., Aq.
    const S = 300;
    const houses = groupByHouse(chart);
    const ascSignIdx = chart.ascendant?.sign_idx ?? 0;

    // Fixed sign-idx per box in the 4x4 (south indian traditional layout)
    // Row/col: (row 0 top, col 0 left)
    const GRID = [
        // row 0: Pi Ar Ta Ge
        [11, 0, 1, 2],
        // row 1: Aq . . Cn
        [10, null, null, 3],
        // row 2: Cp . . Le
        [9, null, null, 4],
        // row 3: Sg Sc Li Vi
        [8, 7, 6, 5],
    ];

    const cellSize = 70;
    const ox = (S - cellSize * 4) / 2;
    const oy = (S - cellSize * 4) / 2;

    return (
        <svg viewBox={`0 0 ${S} ${S}`} width={size} height={size} data-testid="kundali-chart-south" className="mx-auto">
            <rect x="0" y="0" width={S} height={S} fill={COLOR.bg} />
            {GRID.flatMap((row, ri) =>
                row.map((signIdx, ci) => {
                    if (signIdx === null) return null;
                    const x = ox + ci * cellSize;
                    const y = oy + ri * cellSize;
                    // House # from ascendant: (signIdx - ascSignIdx + 12) % 12 + 1
                    const h = ((signIdx - ascSignIdx + 12) % 12) + 1;
                    const planets = houses[h - 1];
                    const isAsc = h === 1;
                    return (
                        <g key={`${ri}-${ci}`}>
                            <rect x={x} y={y} width={cellSize} height={cellSize}
                                fill={isAsc ? 'rgba(245,158,11,0.06)' : 'transparent'}
                                stroke={COLOR.line} strokeWidth="1.2" />
                            <text x={x + 5} y={y + 12} fill={COLOR.zodiac} fontSize="9"
                                fontFamily="Cormorant Garamond" opacity="0.8">
                                {SIGN_SHORT[signIdx]}
                            </text>
                            <text x={x + cellSize - 5} y={y + 12} fill={COLOR.house} fontSize="9"
                                textAnchor="end" opacity="0.5">
                                {h}
                            </text>
                            {isAsc && (
                                <text x={x + cellSize / 2} y={y + 24} fill={COLOR.asc} fontSize="8"
                                    textAnchor="middle" fontFamily="Cormorant Garamond">
                                    Asc
                                </text>
                            )}
                            {planets.map((p, idx) => (
                                <text
                                    key={p.name}
                                    x={x + cellSize / 2}
                                    y={y + 38 + idx * 10}
                                    fill={p.retrograde ? '#EF4444' : COLOR.planet}
                                    fontSize="10"
                                    fontFamily="Cormorant Garamond"
                                    textAnchor="middle"
                                >
                                    {PLANET_SHORT[p.name] || p.name.slice(0, 2)}{p.retrograde ? '℞' : ''}
                                </text>
                            ))}
                        </g>
                    );
                })
            )}
        </svg>
    );
}
